import { useEffect, useMemo, useState } from 'react';

import { NetworkStatus, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { ApolloError } from 'apollo-client';

import { useParams } from 'js/lib/useRouter';

import useIsMounted from 'bundles/common/hooks/useIsMounted';
import submitterListQuery from 'bundles/grading/api/SubmitterListQuery.graphql';
import type { GradingItemTask, SubmitterListQuery, SubmitterListQueryVariables } from 'bundles/grading/api/types';
import {
  submitterFilterMatch,
  useSearchContext,
} from 'bundles/grading/components/staff-graded-assignments/left-panel/search-and-filters/SearchContext';
import type { SGASubmitter } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { hasSubmission } from 'bundles/grading/utils/submissionUtils';
import { constructSGASubmitter, sortSubmitterList } from 'bundles/grading/utils/submitterUtils';

import _t from 'i18n!nls/grading';

type DataProcessed = {
  /**
   * Current group ID
   */
  currentGroupId: string;
  /**
   * Is team assignment?
   */
  isTeamAssignment: boolean;
  /**
   * List of submitters (individual or teams)
   */
  submitters: SGASubmitter[];
  /**
   * Current submitter details
   */
  currentSubmitter: SGASubmitter | null;
};

type DataFiltered = {
  /**
   * Filtered list of submitters with submission
   */
  submittedGroup: SGASubmitter[];
  /**
   * Filtered list of submitters with no-submission
   */
  notSubmittedGroup: SGASubmitter[];
  /**
   * Previous submitter details, null if current submitter is first
   */
  previousSubmitter: SGASubmitter | null;
  /**
   * Next submitter details, null if current submitter is last
   */
  nextSubmitter: SGASubmitter | null;
};

export type SubmitterListProcessed = DataProcessed & DataFiltered;

export const QUERY_LIMIT = 400;

/**
 * Generates an array of offsets, skipping the 0-th page (it was fetched with
 * initial useQuery).
 */
export const getOffsets = (totalRecords: number, limit: number) => {
  const totalPages = Math.ceil(totalRecords / limit);
  return Array.from(Array(totalPages - 1), (_, i) => (i + 1) * limit);
};

export type UseSubmitterListQueryResult = {
  loading: boolean;
  data?: SubmitterListProcessed;
  error?: ApolloError;
};

/**
 * This hook sends a query to fetch a list of submitters
 */
const useSubmitterListQuery = (
  variables: Omit<SubmitterListQueryVariables, 'limit' | 'cursor'>,
  skip: boolean = false
): UseSubmitterListQueryResult => {
  const [err, setErr] = useState<ApolloError>();
  const isMounted = useIsMounted();

  const routeParams = useParams();

  const searchContext = useSearchContext();
  const searchKeyword = searchContext.searchKeyword.toLocaleLowerCase();
  const filters = searchContext.filters;

  // "Grading/Assignments" entry point route uses "id";
  // "Learners/Rosters" route uses "userId".
  const currentSubmitterId = routeParams.id || routeParams.userId;

  const onCompleted = (queryResponse: SubmitterListQuery) => {
    if (!queryResponse?.GroupGrading?.queryGroup?.items.length) {
      setErr(new ApolloError({ errorMessage: _t('Grading task is not found.') }));
    }
  };

  const result = useQuery<SubmitterListQuery, SubmitterListQueryVariables>(submitterListQuery, {
    variables: {
      ...variables,
      limit: QUERY_LIMIT,
    },
    context: { clientName: 'gatewayGql' },
    skip,
    notifyOnNetworkStatusChange: true,
    onCompleted,
    onError: (error) => {
      setErr(error);
      Sentry.captureException('Error fetching SubmitterList in Quick Grader', {
        extra: { error, variables: JSON.stringify(variables) },
      });
    },
  });

  const { cursor, totalElements } =
    result.data?.GroupGrading?.queryGroup?.items[0]?.paginatedGradingTasks?.pagination || {};

  const loadingAll = useMemo(() => {
    return !skip && (cursor !== null || result.loading || result.networkStatus === NetworkStatus.fetchMore);
  }, [skip, cursor, result.loading, result.networkStatus]);

  // Recalculate `dataProcessed` object when:
  // - `useQuery` results in updated `response`;
  // - `params` part of the route is changed, specifically submitterId.
  const dataProcessed: DataProcessed | undefined = useMemo(() => {
    if (loadingAll) {
      return undefined;
    }
    if (!result.data?.GroupGrading?.queryGroup?.items.length) {
      return undefined;
    }

    const currentContextId = routeParams.contextId;

    // grab response items to easily parse through
    const item = result.data.GroupGrading.queryGroup.items[0];
    const isAnonymized = Boolean(item?.hasAnonymousGrading);
    const elements = item?.paginatedGradingTasks.elements;
    // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    const isTeamAssignment = Boolean(elements[0]?.submitterId.startsWith('team~'));

    let submitters: SGASubmitter[] = [];
    let currentSubmitter: SGASubmitter | null = null;

    // ensuring items are not undefined from response before creating submitter objects
    if (item) {
      // TODO:
      // 1 - reassess `id` value for the case of team assignment in Learner/Roster
      // flow. Quick Grader is not supported in the Learner/Roster flow.
      // 2 - Will need to transition into new team url using submitterId only.
      // Consider a separate query for Learner/Roster flow to further filter userId
      // in use.
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      submitters = elements.map((task: GradingItemTask) => {
        // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
        const submitter = constructSGASubmitter(task, currentContextId, isTeamAssignment, isAnonymized);
        if (submitter.id === currentSubmitterId) {
          currentSubmitter = submitter;
        }
        return submitter;
      });

      submitters = sortSubmitterList(submitters);
    }

    return {
      currentGroupId: result.data.GroupGrading.queryGroup.groupId,
      isTeamAssignment,
      submitters,
      currentSubmitter,
    };
  }, [currentSubmitterId, result.data, loadingAll, routeParams.contextId]);

  // Recalculate `dataFiltered` object when:
  // - `dataProcessed` changes;
  // - `filters` or `search` changes.
  // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  const dataFiltered = useMemo<DataFiltered | undefined>(() => {
    if (!dataProcessed) {
      return undefined;
    }
    const submittedGroup: SGASubmitter[] = [];
    const notSubmittedGroup: SGASubmitter[] = [];
    dataProcessed.submitters.forEach((submitter) => {
      // sort out submitted/not-submitted groups
      if (hasSubmission(submitter.submittedAt)) {
        if (submitterFilterMatch(submitter, searchKeyword, filters)) {
          submittedGroup.push(submitter);
        }
      } else if (submitterFilterMatch(submitter, searchKeyword, filters)) {
        notSubmittedGroup.push(submitter);
      }
      return submitter;
    });

    let previousSubmitter = null;
    let nextSubmitter = null;
    let isCurrentInTheList = false;

    const allFiltered = submittedGroup.concat(notSubmittedGroup);
    allFiltered.find((submitter, index, combinedGroups) => {
      if (submitter.id === dataProcessed.currentSubmitter?.id) {
        isCurrentInTheList = true;
        if (index > 0) previousSubmitter = combinedGroups[index - 1];
        if (index < combinedGroups.length - 1) nextSubmitter = combinedGroups[index + 1];
        return true;
      } else {
        return false;
      }
    });
    if (!isCurrentInTheList) {
      nextSubmitter = allFiltered[0];
    }

    return {
      submittedGroup,
      notSubmittedGroup,
      previousSubmitter,
      nextSubmitter,
    };
  }, [dataProcessed, searchKeyword, filters]);

  useEffect(() => {
    async function fetchAll(totalRecords: number, limit: number) {
      try {
        // Create a `fetchMore` promise for each offset.
        const offsets = getOffsets(totalRecords, limit);
        const promises = offsets.map((offset) =>
          result.fetchMore({
            variables: { cursor: `${offset}` },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              const updatedResult = {
                ...prevResult,
                GroupGrading: {
                  ...prevResult.GroupGrading,
                  queryGroup: {
                    ...prevResult.GroupGrading?.queryGroup,
                    items: [
                      {
                        ...prevResult.GroupGrading?.queryGroup?.items[0],
                        paginatedGradingTasks: {
                          // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                          ...prevResult.GroupGrading?.queryGroup?.items[0].paginatedGradingTasks,
                          elements: [
                            // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                            ...(prevResult.GroupGrading?.queryGroup?.items[0].paginatedGradingTasks.elements || []),
                            // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                            ...(fetchMoreResult.GroupGrading?.queryGroup?.items[0].paginatedGradingTasks.elements ||
                              []),
                          ],
                          pagination: {
                            // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                            ...fetchMoreResult.GroupGrading?.queryGroup?.items[0].paginatedGradingTasks.pagination,
                            cursor: null,
                          },
                        },
                      },
                    ],
                  },
                },
              } as SubmitterListQuery;

              return updatedResult;
            },
          })
        );
        await Promise.all(promises);
      } catch (e) {
        if (isMounted()) {
          setErr(e);
        }
      }
    }

    if (cursor && totalElements) {
      fetchAll(totalElements, QUERY_LIMIT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, totalElements, result.fetchMore]);

  return {
    loading: loadingAll,
    data: dataProcessed && dataFiltered ? { ...dataProcessed, ...dataFiltered } : undefined,
    error: err,
  };
};

export default useSubmitterListQuery;
